import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useDailyMood } from "@/hooks/useDailyMood";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Trophy, Star, MessageSquare } from "lucide-react";

const MOOD_EMOJIS = {
  1: "😢",
  2: "😕",
  3: "😐",
  4: "🙂",
  5: "😊",
};

const REFLECTION_PROMPTS = {
  low: [
    "What specific events influenced your mood today?",
    "Which coping strategies might help right now?",
    "Is there someone you could reach out to for support?",
  ],
  high: [
    "What made today particularly good?",
    "How can you build on this positive energy?",
    "Who could you share this positive moment with?",
  ],
};

const ACHIEVEMENTS = {
  firstEntry: {
    title: "First Step",
    description: "Completed your first mood check-in",
    icon: Star,
  },
  threeDay: {
    title: "Building Momentum",
    description: "3-day check-in streak",
    icon: Trophy,
  },
  sevenDay: {
    title: "Consistent Awareness",
    description: "7-day check-in streak",
    icon: Trophy,
  },
};

const DailyMoodTracker = () => {
  const { isLoading, todaysMood, saveDailyMood } = useDailyMood();
  const [selectedMood, setSelectedMood] = useState<number>(todaysMood?.mood_score || 3);
  const [note, setNote] = useState(todaysMood?.note || "");
  const [isSaving, setIsSaving] = useState(false);
  const [streak, setStreak] = useState(0); // This would ideally be fetched from the database

  const getReflectionPrompt = () => {
    if (selectedMood <= 2) {
      return REFLECTION_PROMPTS.low[Math.floor(Math.random() * REFLECTION_PROMPTS.low.length)];
    } else if (selectedMood >= 4) {
      return REFLECTION_PROMPTS.high[Math.floor(Math.random() * REFLECTION_PROMPTS.high.length)];
    }
    return "How are you feeling? Feel free to share more details about your day.";
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await saveDailyMood({
        mood_score: selectedMood,
        emoji: MOOD_EMOJIS[selectedMood as keyof typeof MOOD_EMOJIS],
        note,
        date: new Date().toISOString().split('T')[0],
      });
      
      // Increment streak (in a real app, this would be handled by the backend)
      setStreak(prev => prev + 1);
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <Card className="p-4 space-y-4">
        <Skeleton className="h-8 w-48" />
        <div className="flex gap-2">
          {[...Array(5)].map((_, i) => (
            <Skeleton key={i} className="h-12 w-12" />
          ))}
        </div>
        <Skeleton className="h-24 w-full" />
      </Card>
    );
  }

  return (
    <Card className="p-4 space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">How are you feeling today?</h3>
        <div className="flex gap-2">
          {streak >= 7 && (
            <Badge variant="secondary" className="flex items-center gap-1">
              <Trophy className="h-4 w-4" />
              7-day streak!
            </Badge>
          )}
          {streak >= 3 && streak < 7 && (
            <Badge variant="secondary" className="flex items-center gap-1">
              <Star className="h-4 w-4" />
              {streak} day streak!
            </Badge>
          )}
          {streak === 1 && (
            <Badge variant="secondary" className="flex items-center gap-1">
              <Star className="h-4 w-4" />
              First entry!
            </Badge>
          )}
        </div>
      </div>
      
      <div className="flex gap-2 justify-between">
        {Object.entries(MOOD_EMOJIS).map(([score, emoji]) => (
          <Button
            key={score}
            variant={selectedMood === Number(score) ? "secondary" : "outline"}
            className="h-12 w-12 text-2xl"
            onClick={() => setSelectedMood(Number(score))}
          >
            {emoji}
          </Button>
        ))}
      </div>

      <div className="space-y-2">
        <Label htmlFor="mood-note" className="flex items-center gap-2">
          <MessageSquare className="h-4 w-4" />
          {getReflectionPrompt()}
        </Label>
        <Textarea
          id="mood-note"
          placeholder="Share your thoughts..."
          value={note}
          onChange={(e) => setNote(e.target.value)}
          className="min-h-[100px]"
        />
      </div>

      <Button 
        onClick={handleSave} 
        disabled={isSaving}
        className="w-full"
      >
        {isSaving ? "Saving..." : "Save Today's Mood"}
      </Button>
    </Card>
  );
};

export default DailyMoodTracker;